import { OrganizerEventResponse, OrganizerEvent, EventbriteApiResponse, EventbriteApiEvent } from './types';
import type { EventInteraction, EventInteractionWithContext } from '@/tracking/types';

const NUMBER_OF_MORE_EVENTS_TO_SHOW = 3;

// Transform Eventbrite API response to internal format
export const transformApiResponseToOrganizerEvents = ({
  apiResponse,
  currentEventId,
}: {
  apiResponse: EventbriteApiResponse | null;
  currentEventId: string;
}): OrganizerEventResponse | null => {
  if (!apiResponse?.events?.length) {
    return null;
  }

  const transformedEvents: OrganizerEvent[] = apiResponse.events.map((event: EventbriteApiEvent) => ({
    id: event.id,
    name: event.name?.text || '',
    url: event.url,
    imageUrl: event.logo?.url || '',
    startDate: event.start?.local?.split('T')[0] || '',
    startTime: event.start?.local?.split('T')[1]?.slice(0, 8) || '',
    timezone: event.start?.timezone || '',
    isFree: event.is_free || false,
    isOnlineEvent: event.online_event || false,
    isPromoted: false, // API doesn't provide this info directly
    formattedPriceString: formatPriceFromTicketAvailability(event.ticket_availability),
    venue: undefined, // Would need venue API call to populate this
    urgencySignals: {}, // API doesn't provide urgency signals directly
    specialDiscounts: { hasBogoTickets: false }, // API doesn't provide this info directly
    ticket_availability: {
      has_available_tickets: event.ticket_availability?.has_available_tickets || false,
    },
  }));

  const filteredEvents = getMoreEventsFromThisOrganizerFiltered({
    currentEventId,
    events: transformedEvents,
  });

  return {
    events: filteredEvents,
    unrecoverableEventIds: [], // API v3 doesn't provide this info
  };
};

// Helper function to format price from ticket availability
const formatPriceFromTicketAvailability = (
  ticketAvailability?: EventbriteApiEvent['ticket_availability'],
): string | undefined => {
  if (!ticketAvailability) return undefined;

  if (ticketAvailability.minimum_ticket_price) {
    const minPrice = ticketAvailability.minimum_ticket_price;
    return minPrice.display;
  }

  return undefined;
};

// Get events to show (limit to 4)
export const getEventsToShow = (
  organizerEvents: OrganizerEventResponse | null,
  currentEventId: string,
): OrganizerEvent[] => {
  if (!organizerEvents?.events?.length) {
    return [];
  }
  // Filter out the current event and limit to 4
  return organizerEvents.events.filter((event) => event.id !== currentEventId).slice(0, 4);
};

// Create tracking information getter
export const createTrackingInformationGetter = (
  mainEvent: { id?: string; isOnline?: boolean; isEnded?: boolean },
  allEvents: OrganizerEvent[] = [],
  locale: string,
  sessionInfo: {
    userId?: string | null;
    sessionId?: string | null;
    guestId?: string | null;
  },
) => {
  return (eventInteraction: EventInteraction): EventInteractionWithContext => {
    // Find the specific event from the events array for rank calculation
    const eventToTrack = allEvents.find((e) => e.id === eventInteraction.id);

    if (!eventToTrack) {
      console.error(`Event with id ${eventInteraction.id} not found in organizer events list`);
      return {} as EventInteractionWithContext;
    }

    const rank = allEvents.findIndex((e) => e.id === eventInteraction.id) + 1;

    return {
      id: eventInteraction.id,
      action: eventInteraction.action,
      event: eventInteraction.event,
      user: {
        id: sessionInfo.userId ?? undefined,
        locale,
        guestId: sessionInfo.guestId ?? undefined,
        sessionId: sessionInfo.sessionId ?? undefined,
      },
      place: {
        name: 'more_organizer_events',
        position: rank,
        page: 1,
        context: {
          main_event_id: mainEvent.id,
          is_online: mainEvent.isOnline,
          is_ended: mainEvent.isEnded,
          total_organizer_events: allEvents.length,
        },
      },
    };
  };
};

export const getMoreEventsFromThisOrganizerFiltered = ({
  currentEventId,
  events,
}: {
  currentEventId: string;
  events: OrganizerEvent[];
}): OrganizerEvent[] => {
  return (
    events
      ?.filter(
        (event: OrganizerEvent) => event.ticket_availability?.has_available_tickets && event.id !== currentEventId,
      )
      .slice(0, NUMBER_OF_MORE_EVENTS_TO_SHOW) || []
  );
};
